import * as Sentry from '@sentry/react';
import type { AxiosInstance, AxiosError } from 'axios';
import { getAuthSessionHandlers } from '@/shared/api/auth-session-handlers';

const SESSION_INVALIDATED = 'SESSION_INVALIDATED';

let redirectPending = false;

export function isSessionInvalidatedError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if ('response' in error) {
    const axiosErr = error as AxiosError<{ code?: string }>;
    if (axiosErr.response?.status === 401) {
      return axiosErr.response.data?.code === SESSION_INVALIDATED;
    }
  }
  return (
    'status' in error &&
    (error as Record<string, unknown>)['status'] === 401 &&
    'code' in error &&
    (error as Record<string, unknown>)['code'] === SESSION_INVALIDATED
  );
}

function handleForcedLogout(reason: string): void {
  if (redirectPending) return;
  redirectPending = true;

  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Session invalidated by server',
    data: { reason, path: window.location.pathname },
    level: 'warning',
  });

  getAuthSessionHandlers().logout();

  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/login')) {
    redirectPending = false;
    return;
  }

  window.location.href = `/login?reason=${encodeURIComponent(reason)}`;
}

export function applySessionInvalidationInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ code?: string }>) => {
      if (error.response?.status === 401) {
        const code = error.response.data?.code;
        if (code === SESSION_INVALIDATED) {
          handleForcedLogout(code);
        }
      }
      return Promise.reject(error);
    },
  );
}
